import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MessageSquare, Bell, Users, Sparkles, ChevronLeft, ChevronRight, Tractor, UsersRound, Megaphone, Briefcase, Radio, LogOut, Video, User } from "lucide-react";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AuthModal } from "./AuthModal";
import { useTagTeamRequestsCount } from "@/hooks/useTagTeamRequestsCount";

type Farmer = {
  id: string;
  name: string;
  avatarUrl?: string;
  city?: string;
  distanceKm?: number;
  crops?: string[];
  posts7d?: number;         // activity score
  mutualTeams?: number;
};

type NotificationItem = {
  id: string;
  kind: "mention" | "comment" | "follow" | "system";
  title: string;
  preview?: string;
  ts: string;               // ISO
  unread?: boolean;
  link?: string;
};

type MessageThread = {
  id: string;
  peerId: string;
  peerName: string;
  avatarUrl?: string;
  lastSnippet: string;
  ts: string;               // ISO
  unreadCount?: number;
};

type TabKey = "active" | "messages";

export function LeftConsole({
  activeNearby = [],
  suggested = [],
  notifications = [],
  threads = [],
  onFollow,
  onMessage,
  defaultTab = "active",
  className = "",
  onCollapsedChange,
  user,
  onNavigateToDiscover,
}: {
  activeNearby?: Farmer[];
  suggested?: Farmer[];
  notifications?: NotificationItem[];
  threads?: MessageThread[];
  onFollow?: (farmerId: string) => void;
  onMessage?: (farmerId: string) => void;
  defaultTab?: TabKey;
  className?: string;
  onCollapsedChange?: (collapsed: boolean) => void;
  user?: SupabaseUser | null;
  onNavigateToDiscover?: () => void;
}) {
  const [tab, setTab] = useState<TabKey>(defaultTab);
  const [collapsed, setCollapsed] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const navigate = useNavigate();
  const tagRequestsCount = useTagTeamRequestsCount(user);

  // Fetch user's profile avatar
  useEffect(() => {
    if (!user) {
      setUserAvatar(null);
      return;
    }

    const fetchUserAvatar = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('avatar_url, farm_images')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        // Try to get profile_icon from farm_images first
        const farmImages = profile.farm_images && Array.isArray(profile.farm_images) 
          ? profile.farm_images 
          : [];
        const profileIcon = farmImages.find((img: any) => img.role === 'profile_icon');
        const avatarUrl = (profileIcon as any)?.url || profile.avatar_url;
        setUserAvatar(avatarUrl);
      }
    };

    fetchUserAvatar();
  }, [user]);

  // Notify parent when collapsed state changes
  useEffect(() => {
    onCollapsedChange?.(collapsed);
  }, [collapsed, onCollapsedChange]);

  // keyboard cycle Alt+[ / Alt+]
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      if (e.key !== "[" && e.key !== "]") return;
      e.preventDefault();
      const order: TabKey[] = ["active", "messages"];
      const i = order.indexOf(tab);
      setTab(e.key === "]" ? order[(i + 1) % order.length] : order[(i - 1 + order.length) % order.length]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab]);

  const counts = useMemo(() => ({
    notifs: notifications.filter(n => n.unread).length,
    messages: threads.reduce((s,t)=>s+(t.unreadCount||0),0),
    tagRequests: tagRequestsCount
  }), [notifications, threads, tagRequestsCount]);

  const railW = collapsed ? "w-[80px]" : "w-[240px]";

  return (
    <aside 
      className={`sticky z-10 overflow-y-auto border-r border-border/30 shadow-lg transition-[width] duration-200 ${railW} ${className} flex flex-col shrink-0`}
      style={{ 
        background: 'var(--left-rail-gradient)',
        top: 'var(--ys-app-header-h, 0px)',
        height: 'calc(100svh - var(--ys-app-header-h, 0px))'
      }}
    >
      {/* Vertical gloss for subtle shine */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-[linear-gradient(to_right,rgba(255,255,255,0.25),transparent)]" />
      
      {/* header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-border/20">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Activity</span>
        <button className="rounded p-1 hover:bg-muted" onClick={()=>setCollapsed(v=>!v)} aria-label="Toggle">
          {collapsed ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
        </button>
      </div>

      {/* navigation shortcuts */}
      <nav className="px-2 py-2 border-b border-border/20 space-y-1">
        <NavShortcut 
          icon={
            userAvatar ? (
              <img 
                src={userAvatar} 
                alt="Profile" 
                className="w-4 h-4 rounded-full object-cover"
              />
            ) : (
              <Tractor size={16}/>
            )
          } 
          label="Profile" 
          to="/profile" 
          collapsed={collapsed}
          requireAuth
          user={user}
          onAuthRequired={() => setShowAuthModal(true)}
        />
        <NavShortcut icon={<UsersRound size={16}/>} label="TAG Teams" to="/tag-teams" collapsed={collapsed} disabled={!user} />
        <NavShortcut icon={<Briefcase size={16}/>} label="Grants" to="/grants" collapsed={collapsed} disabled={!user} />
        <NavShortcut icon={<Megaphone size={16}/>} label="Create Announcement" to="/compose" collapsed={collapsed} disabled={!user} />
        <NavShortcut icon={<Radio size={16}/>} label="Advertise" to="/advertise" collapsed={collapsed} />
      </nav>

      {/* tabs */}
      <nav className={`px-2 pt-2 ${collapsed ? "" : "space-y-1"}`}>
        <TabBtn icon={<Users size={16}/>}   label="Most Active Nearby" active={tab==="active"}    collapsed={collapsed} onClick={()=>setTab("active")}/>
        <NavShortcut 
          icon={<Sparkles size={16}/>} 
          label="Suggested For You" 
          to="/" 
          collapsed={collapsed}
          onClick={(e: React.MouseEvent) => {
            console.log("NavShortcut clicked, onNavigateToDiscover type:", typeof onNavigateToDiscover);
            e.preventDefault();
            if (onNavigateToDiscover) {
              onNavigateToDiscover();
            } else {
              console.warn("onNavigateToDiscover is not defined");
            }
          }}
        />
        {user && (
          <>
            <NavShortcut 
              icon={<UsersRound size={16}/>} 
              label="tAG Teams Requests" 
              to="/tag-teams?tab=received" 
              collapsed={collapsed}
              badge={counts.tagRequests}
            />
            <NavShortcut 
              icon={<Video size={16}/>} 
              label="Responses to Posts" 
              to="/profile?tab=video-responses" 
              collapsed={collapsed}
              badge={counts.notifs}
            />
          </>
        )}
        <TabBtn icon={<MessageSquare size={16}/>} label="Messages"     badge={counts.messages}   active={tab==="messages"} collapsed={collapsed} onClick={()=>setTab("messages")}/>
      </nav>

      {/* lists */}
      <div className="flex-1 overflow-auto px-2 pb-2">
        {tab === "active" && <FarmerList items={activeNearby} collapsed={collapsed} onFollow={onFollow} onMessage={onMessage}/>}
        {tab === "messages" && <ThreadList items={threads} collapsed={collapsed}/>}
      </div>

      {/* Contact and Legal sections at bottom */}
      <div className="mt-auto border-t border-border/20 px-2 py-3 space-y-4">
        {!collapsed ? (
          <>
            {/* Auth section - Log In or Log Out */}
            <div>
              {user ? (
                <button
                  onClick={async () => {
                    try {
                      await supabase.auth.signOut();
                      toast.success("Logged out successfully");
                    } catch (error) {
                      toast.error("Failed to log out");
                      console.error('Logout error:', error);
                    }
                  }}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground py-1 w-full"
                >
                  <LogOut size={14} />
                  <span>Log Out</span>
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground py-1 w-full"
                >
                  <User size={14} />
                  <span>Sign In</span>
                </button>
              )}
            </div>
            
            {/* Contact section */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">Contact</p>
              <nav className="space-y-1">
                <a href="mailto:privacy@yieldsocial.com" className="block text-xs text-muted-foreground hover:text-foreground py-1">
                  privacy@yieldsocial.com
                </a>
                <a href="mailto:nancy@truesty.com" className="block text-xs text-muted-foreground hover:text-foreground py-1">
                  Support
                </a>
              </nav>
            </div>
            
            {/* Legal section */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">Legal</p>
              <nav className="space-y-1">
                <NavLink to="/terms" className="block text-xs text-muted-foreground hover:text-foreground py-1">
                  Terms of Service
                </NavLink>
                <NavLink to="/privacy-policy" className="block text-xs text-muted-foreground hover:text-foreground py-1">
                  Privacy Policy
                </NavLink>
                <NavLink to="/cookies" className="block text-xs text-muted-foreground hover:text-foreground py-1">
                  Cookie Policy
                </NavLink>
                <NavLink to="/data-deletion" className="block text-xs text-muted-foreground hover:text-foreground py-1">
                  Data Deletion
                </NavLink>
              </nav>
            </div>
            
            <p className="text-[10px] text-muted-foreground">
              © {new Date().getFullYear()} YieldSocial. All rights reserved.
            </p>
          </>
        ) : (
          <div className="flex flex-col gap-2 items-center">
            {/* Auth button - collapsed state */}
            {user ? (
              <button
                onClick={async () => {
                  try {
                    await supabase.auth.signOut();
                    toast.success("Logged out successfully");
                  } catch (error) {
                    toast.error("Failed to log out");
                    console.error('Logout error:', error);
                  }
                }}
                className="text-muted-foreground hover:text-foreground"
                title="Log Out"
              >
                <LogOut size={16} />
              </button>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="text-muted-foreground hover:text-foreground"
                title="Sign In"
              >
                <User size={16} />
              </button>
            )}
            <span className="text-xs text-muted-foreground" title="Contact">✉️</span>
            <span className="text-xs text-muted-foreground" title="Legal">⚖️</span>
          </div>
        )}
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        feature="Profile"
        onBrowseAnonymously={() => setShowAuthModal(false)}
      />
    </aside>
  );
}

/* --- subcomponents --- */

function TabBtn({icon,label,active,collapsed,onClick,badge}:{icon:React.ReactNode,label:string,active:boolean,collapsed:boolean,onClick:()=>void,badge?:number}) {
  return (
    <button onClick={onClick}
      className={`relative flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-muted ${active ? "bg-muted font-medium" : ""}`}>
      <span className={`${collapsed ? "mx-auto" : "mr-2"}`}>{icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
      {!!badge && !collapsed && <span className="ml-auto rounded bg-primary/15 px-1.5 text-[10px] text-primary">{badge}</span>}
    </button>
  );
}

function Avatar({src,alt}:{src?:string;alt:string}) {
  return <img src={src || "/img/avatar-fallback.png"} alt={alt} className="h-7 w-7 rounded-full object-cover"/>;
}

function FarmerList({items,collapsed,onFollow,onMessage}:{items:Farmer[];collapsed:boolean;onFollow?:(id:string)=>void;onMessage?:(id:string)=>void;}) {
  if (!items.length) return <Empty label="No farmers yet." />;
  return (
    <ul className="space-y-1">
      {items.map(f=>(
        <li key={f.id} className="rounded-md border border-transparent p-2 hover:border-muted">
          <div className="flex items-center gap-2">
            <Avatar src={f.avatarUrl} alt={f.name}/>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <NavLink to={`/farm/${f.id}`} className="truncate font-medium hover:underline">{f.name}</NavLink>
                {typeof f.distanceKm === "number" && <span className="text-xs text-muted-foreground">{f.distanceKm.toFixed(0)} km</span>}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {f.city || ""}{f.city && f.crops?.length ? " • " : ""}{f.crops?.slice(0,2).join(", ")}
              </div>
            </div>
            {!collapsed && (
              <div className="flex gap-2">
                <button className="rounded border px-2 py-1 text-xs hover:bg-muted" onClick={()=>onFollow?.(f.id)}>Follow</button>
                <button className="rounded border px-2 py-1 text-xs hover:bg-muted" onClick={()=>onMessage?.(f.id)}>Message</button>
              </div>
            )}
          </div>
          {typeof f.posts7d === "number" && <div className="mt-1 text-[10px] text-muted-foreground">Activity (7d): {f.posts7d}</div>}
        </li>
      ))}
    </ul>
  );
}

function NotifList({items,collapsed}:{items:NotificationItem[];collapsed:boolean;}) {
  if (!items.length) return <Empty label="No notifications" />;
  return (
    <ul className="space-y-1">
      {items.map(n=>(
        <li key={n.id}>
          <NavLink to={n.link || "#"} className="block rounded-md border border-transparent p-2 hover:border-muted">
            <div className="flex items-center justify-between">
              <div className="truncate font-medium">{n.title}</div>
              <span className="ml-2 shrink-0 text-[10px] text-muted-foreground">{new Date(n.ts).toLocaleTimeString()}</span>
            </div>
            {n.preview && <div className="truncate text-sm text-muted-foreground">{n.preview}</div>}
            {n.unread && <span className="mt-1 inline-block rounded bg-primary/15 px-1.5 py-0.5 text-[10px] text-primary">unread</span>}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function ThreadList({items,collapsed}:{items:MessageThread[];collapsed:boolean;}) {
  if (!items.length) return <Empty label="No messages" />;
  return (
    <ul className="space-y-1">
      {items.map(t=>(
        <li key={t.id} className="rounded-md border border-transparent p-2 hover:border-muted">
          <NavLink to={`/messages/${t.id}`} className="flex items-center gap-2">
            <Avatar src={t.avatarUrl} alt={t.peerName}/>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{t.peerName}</span>
                {!!t.unreadCount && <span className="rounded bg-primary/15 px-1.5 text-[10px] text-primary">{t.unreadCount}</span>}
              </div>
              <div className="truncate text-sm text-muted-foreground">{t.lastSnippet}</div>
            </div>
            <span className="ml-2 shrink-0 text-[10px] text-muted-foreground">{new Date(t.ts).toLocaleTimeString()}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function Empty({label}:{label:string}) {
  return <div className="mt-6 text-center text-sm text-muted-foreground">{label}</div>;
}

function NavShortcut({
  icon,
  label,
  to,
  collapsed,
  disabled=false,
  requireAuth=false,
  user,
  onAuthRequired,
  badge,
  onClick
}:{
  icon:React.ReactNode;
  label:string;
  to:string;
  collapsed:boolean;
  disabled?:boolean;
  requireAuth?:boolean;
  user?:SupabaseUser | null;
  onAuthRequired?:()=>void;
  badge?:number;
  onClick?:(e: React.MouseEvent) => void;
}) {
  const baseClasses = "relative flex w-full items-center rounded-md px-2 py-2 text-sm";
  const enabledClasses = "hover:bg-muted";
  const disabledClasses = "opacity-40 cursor-not-allowed";
  
  if (disabled) {
    return (
      <div className={`${baseClasses} ${disabledClasses}`}>
        <span className={`${collapsed ? "mx-auto" : "mr-2"}`}>{icon}</span>
        {!collapsed && <span className="truncate">{label}</span>}
      </div>
    );
  }
  
  // If auth is required and user is not logged in, show as button that triggers auth modal
  if (requireAuth && !user) {
    return (
      <button
        onClick={(e) => {
          e.preventDefault();
          onAuthRequired?.();
        }}
        className={`${baseClasses} ${enabledClasses}`}
      >
        <span className={`${collapsed ? "mx-auto" : "mr-2"}`}>{icon}</span>
        {!collapsed && <span className="truncate">{label}</span>}
      </button>
    );
  }
  
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({isActive}) => 
        `${baseClasses} ${enabledClasses} ${isActive ? "bg-muted font-medium" : ""}`
      }
    >
      <span className={`${collapsed ? "mx-auto" : "mr-2"}`}>{icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
      {!!badge && !collapsed && <span className="ml-auto rounded bg-primary/15 px-1.5 text-[10px] text-primary">{badge}</span>}
    </NavLink>
  );
}

function ChevronRightIcon(){return <ChevronRight className="h-4 w-4" />;}
function ChevronLeftIcon(){return <ChevronLeft className="h-4 w-4" />;}
